import { ColumnDef } from '@tanstack/react-table';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/ui/data-table';
import {
  MoreHorizontal,
  Eye,
  Pencil,
  Trash2,
  Clock,
  CheckCircle2,
  Circle,
  Building2,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ProjectWithStats {
  id: string;
  name: string;
  type: 'client' | 'side-project';
  clientId?: string;
  clientName?: string;
  notes?: string;
  totalTasks: number;
  completedTasks: number;
  totalHours: number;
  earnings: number;
  status: 'not-started' | 'in-progress' | 'completed';
  createdAt: string;
}

interface ProjectsTableProps {
  projects: ProjectWithStats[];
  isLoading?: boolean;
  onDelete?: (project: ProjectWithStats) => void;
  emptyState?: React.ReactNode;
}

const statusConfig = {
  'not-started': {
    label: 'Not Started',
    icon: Circle,
    className: 'text-muted-foreground',
    bgClassName: 'bg-muted/50',
  },
  'in-progress': {
    label: 'In Progress',
    icon: Clock,
    className: 'text-blue-600 dark:text-blue-400',
    bgClassName: 'bg-blue-100 dark:bg-blue-900/30',
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle2,
    className: 'text-green-600 dark:text-green-400',
    bgClassName: 'bg-green-100 dark:bg-green-900/30',
  },
};

const typeConfig = {
  client: {
    label: 'Client',
    icon: Building2,
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  'side-project': {
    label: 'Side Project',
    icon: Sparkles,
    className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  },
};

export function ProjectsTable({
  projects,
  isLoading,
  onDelete,
  emptyState,
}: ProjectsTableProps) {
  const navigate = useNavigate();

  const columns: ColumnDef<ProjectWithStats>[] = [
    {
      accessorKey: 'name',
      header: 'Project',
      cell: ({ row }) => {
        const project = row.original;
        const typeConf = typeConfig[project.type];
        const TypeIcon = typeConf.icon;

        return (
          <div className="flex items-center gap-3 min-w-[200px]">
            <div
              className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center',
                project.type === 'client'
                  ? 'bg-gradient-to-br from-blue-500 to-blue-600'
                  : 'bg-gradient-to-br from-purple-500 to-purple-600'
              )}
            >
              <TypeIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <Link
                to={`/projects/${project.id}`}
                className="font-medium text-foreground hover:text-primary transition-colors"
              >
                {project.name}
              </Link>
              {project.clientName && (
                <p className="text-sm text-muted-foreground">{project.clientName}</p>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => {
        const type = row.getValue('type') as 'client' | 'side-project';
        const config = typeConfig[type];

        return (
          <Badge className={config.className} variant="outline">
            {config.label}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.getValue('status') as keyof typeof statusConfig;
        const config = statusConfig[status];
        const Icon = config.icon;

        return (
          <div className="flex items-center gap-2">
            <Icon className={cn('h-4 w-4', config.className)} />
            <span className={cn('text-sm', config.className)}>{config.label}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'totalTasks',
      header: 'Progress',
      cell: ({ row }) => {
        const project = row.original;
        const progress =
          project.totalTasks > 0
            ? Math.round((project.completedTasks / project.totalTasks) * 100)
            : 0;

        return (
          <div className="min-w-[120px]">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-muted-foreground">
                {project.completedTasks}/{project.totalTasks}
              </span>
              <span className="text-muted-foreground">{progress}%</span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5">
              <div
                className="bg-primary h-1.5 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'totalHours',
      header: 'Hours',
      cell: ({ row }) => {
        const hours = row.getValue('totalHours') as number;
        return (
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>{hours}h</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'earnings',
      header: 'Earnings',
      cell: ({ row }) => {
        const earnings = row.getValue('earnings') as number;
        return (
          <span className="font-medium text-green-600 dark:text-green-400">
            {earnings > 0 ? `€${earnings.toLocaleString()}` : '—'}
          </span>
        );
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const project = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/projects/${project.id}`)}>
                <Eye className="mr-2 h-4 w-4" />
                View
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/projects/${project.id}/edit`)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onDelete?.(project)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={projects}
      isLoading={isLoading}
      emptyState={emptyState}
      pageSize={10}
    />
  );
}
